import React, { useState } from 'react';
import client from '../../api/client';
import usePolling from '../../hooks/usePolling';
import MetricCard from '../common/MetricCard';
import MetricGrid from '../common/MetricGrid';
import DehumidifierControlPanel from '../subsystem/DehumidifierControlPanel';
import StatusBadge from '../common/StatusBadge';
import TimeRangeSelector from '../common/TimeRangeSelector';
import TimeSeriesChart from '../charts/TimeSeriesChart';
import LoadingSpinner from '../common/LoadingSpinner';
import colors from '../../theme/colors';

function notifyUpdate() {
  window.dispatchEvent(new Event('sv360:data-update'));
}

const UNITS = ['dehumidifier1', 'dehumidifier2'];

export default function DehumidifiersPage() {
  const [selected, setSelected] = useState('dehumidifier1');
  const [range, setRange] = useState('-1h');

  const { data: latest, loading } = usePolling(async () => {
    const res = await client.get(`/api/dehumidifiers/latest?dehumidifier=${selected}`);
    notifyUpdate();
    return res.data.sections;
  }, 5000, [selected]);

  const { data: allLatest } = usePolling(async () => {
    if (selected !== 'compare') return null;
    const res = await client.get('/api/dehumidifiers/latest');
    return res.data.dehumidifiers;
  }, 5000, [selected]);

  const { data: tempHistory } = usePolling(async () => {
    const unit = selected === 'compare' ? 'dehumidifier1' : selected;
    const res = await client.get(`/api/dehumidifiers/history?range=${range}&dehumidifier=${unit}&section=sensors&fields=indoor_temp_c,dew_point_temp_c,condenser_temp_c`);
    return res.data.series;
  }, 10000, [range, selected]);

  const { data: humidHistory } = usePolling(async () => {
    const unit = selected === 'compare' ? 'dehumidifier1' : selected;
    const res = await client.get(`/api/dehumidifiers/history?range=${range}&dehumidifier=${unit}&section=sensors&fields=indoor_humidity_pct`);
    const res2 = await client.get(`/api/dehumidifiers/history?range=${range}&dehumidifier=${unit}&section=setpoints&fields=relative_dehumid_sp_pct`);
    return { ...res.data.series, ...res2.data.series };
  }, 10000, [range, selected]);

  if (loading && !latest && !allLatest) return <LoadingSpinner />;

  // Compare mode
  if (selected === 'compare' && allLatest) {
    return (
      <div>
        <SelectorBar selected={selected} setSelected={setSelected} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {UNITS.map(u => {
            const sections = allLatest[u] || {};
            return <UnitCard key={u} unit={u} sections={sections} />;
          })}
        </div>
      </div>
    );
  }

  const operation = latest?.operation || {};
  const sensors = latest?.sensors || {};
  const setpoints = latest?.setpoints || {};
  const alarms = latest?.alarms || {};

  const activeAlarms = Object.entries(alarms)
    .filter(([k, v]) => v === 1 && !k.startsWith('_') && k !== 'section' && k !== 'device' && k !== 'dehumidifier')
    .map(([k]) => k);

  return (
    <div>
      <SelectorBar selected={selected} setSelected={setSelected} />

      {/* Status bar */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <StatusBadge
            label={operation.equipment_status_name || '--'}
            color={statusColor(operation.equipment_status)}
          />
          <StatusBadge
            label={`Compressor: ${operation.compressor_status_name || '--'}`}
            color={statusColor(operation.compressor_status)}
          />
          <StatusBadge
            label={`Fan: ${operation.dehumid_fan_status_name || '--'}`}
            color={statusColor(operation.dehumid_fan_status)}
          />
          <StatusBadge
            label={`Heating: ${operation.heating_status_name || '--'}`}
            color={statusColor(operation.heating_status)}
          />
          <StatusBadge
            label={`Runtime: ${operation.equipment_runtime_hr ?? '--'}h`}
            color="cyan"
          />
          <StatusBadge
            label={`Comp Runtime: ${operation.compressor_runtime_hr ?? '--'}h`}
            color="cyan"
          />
          {activeAlarms.length > 0 && <StatusBadge label={`${activeAlarms.length} Alarms`} color="red" />}
        </div>
      </div>

      {/* Sensor metrics */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">Sensors</div>
        <MetricGrid minWidth={150}>
          <MetricCard label="Indoor Temp" value={sensors.indoor_temp_c} unit="°C" />
          <MetricCard label="Indoor Humidity" value={sensors.indoor_humidity_pct} unit="%" />
          <MetricCard label="Dew Point" value={sensors.dew_point_temp_c} unit="°C" />
          <MetricCard label="Condenser" value={sensors.condenser_temp_c} unit="°C" />
          <MetricCard label="DC Voltage" value={sensors.dc_input_voltage_v} unit="V" />
        </MetricGrid>
      </div>

      {/* Setpoints */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">Setpoints</div>
        <MetricGrid minWidth={150}>
          <MetricCard label="Cooling SP" value={setpoints.cooling_sp_c} unit="°C" />
          <MetricCard label="Dew Point SP" value={setpoints.dew_point_dehumid_temp_sp_c} unit="°C" />
          <MetricCard label="Humidity SP" value={setpoints.relative_dehumid_sp_pct} unit="%" />
        </MetricGrid>
      </div>

      {/* Control Panel */}
      <DehumidifierControlPanel dehumidifier={selected === 'dehumidifier2' ? 'dehumidifier2' : 'dehumidifier1'} />

      {/* Historical charts */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span className="card-title" style={{ marginBottom: 0 }}>Temperature & Dew Point</span>
          <TimeRangeSelector selected={range} onChange={setRange} />
        </div>
        <TimeSeriesChart
          series={tempHistory || {}}
          config={{
            height: 280,
            yAxisLabel: '°C',
            seriesConfig: {
              indoor_temp_c: { name: 'Indoor', unit: '°C', color: colors.series[0] },
              dew_point_temp_c: { name: 'Dew Point', unit: '°C', color: colors.series[1] },
              condenser_temp_c: { name: 'Condenser', unit: '°C', color: colors.series[2] },
            },
          }}
        />
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">Humidity</div>
        <TimeSeriesChart
          series={humidHistory || {}}
          config={{
            height: 240,
            yAxisLabel: '%',
            seriesConfig: {
              indoor_humidity_pct: { name: 'Humidity', unit: '%', color: colors.series[0] },
              relative_dehumid_sp_pct: { name: 'Setpoint', unit: '%', color: colors.series[3], type: 'line' },
            },
          }}
        />
      </div>

      {/* Alarm table */}
      {activeAlarms.length > 0 && (
        <div className="card">
          <div className="card-title">Active Alarms</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {activeAlarms.map(a => (
              <StatusBadge key={a} label={a.replace(/_/g, ' ')} color="red" />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SelectorBar({ selected, setSelected }) {
  return (
    <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
      {UNITS.map(u => (
        <button key={u} onClick={() => setSelected(u)} style={{
          padding: '6px 16px', borderRadius: 6, border: 'none',
          fontSize: 12, fontWeight: 600, cursor: 'pointer',
          background: selected === u ? colors.accent : colors.surface,
          color: selected === u ? colors.bg : colors.textMuted,
        }}>
          {u.replace('dehumidifier', 'Dehum ')}
        </button>
      ))}
      <button onClick={() => setSelected('compare')} style={{
        padding: '6px 16px', borderRadius: 6, border: 'none',
        fontSize: 12, fontWeight: 600, cursor: 'pointer',
        background: selected === 'compare' ? colors.accent : colors.surface,
        color: selected === 'compare' ? colors.bg : colors.textMuted,
      }}>
        Compare
      </button>
    </div>
  );
}

function UnitCard({ unit, sections }) {
  const sensors = sections.sensors || {};
  const operation = sections.operation || {};
  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span className="card-title" style={{ marginBottom: 0 }}>{unit.replace('dehumidifier', 'Dehumidifier ')}</span>
        <StatusBadge label={operation.equipment_status_name || '--'} color={statusColor(operation.equipment_status)} />
      </div>
      <MetricGrid minWidth={120}>
        <MetricCard label="Temp" value={sensors.indoor_temp_c} unit="°C" />
        <MetricCard label="Humidity" value={sensors.indoor_humidity_pct} unit="%" />
        <MetricCard label="Dew Point" value={sensors.dew_point_temp_c} unit="°C" />
        <MetricCard label="Condenser" value={sensors.condenser_temp_c} unit="°C" />
      </MetricGrid>
    </div>
  );
}

// 1=Standby, 2=Operating, 3=Fault
function statusColor(val) {
  if (val === 2) return 'green';
  if (val === 3) return 'red';
  if (val === 1) return 'yellow';
  return 'gray';
}
